import { Director, director, EventTarget, ResolutionPolicy, screen, sys, view, warn, macro, settings } from 'cc';
import Game from './Game';
import Delay from './Toolkit/Utils/Delay';
import { ActivityEventName } from './Config/ActivityConfigType';

export enum Orientation {
    Landscape = 'Landscape',
    Portrait = 'Portrait',
}

enum EventType {
    ORIENTATION_CHANGE_END = 'OrientationChangeEnd',
    ORIENTATION_CHANGE = 'OrientationChange',
    BEFORE_RESIZE = 'BeforeResize',
    RESIZE = 'Resize',
}

export const defaultAdjustDesignResolution = (width: number, height: number, orientation: Orientation, scale: number) => {
    if (orientation === Orientation.Landscape) {
        // 橫式
        if (width / height <= 16 / 9) {
            view.setDesignResolutionSize(1280 * scale, 720 * scale, ResolutionPolicy.SHOW_ALL);
        } else if (width / height < 21 / 9) {
            view.setDesignResolutionSize((width / height) * 720 * scale, 720 * scale, ResolutionPolicy.SHOW_ALL);
        } else {
            view.setDesignResolutionSize(1680 * scale, 720 * scale, ResolutionPolicy.SHOW_ALL);
        }
    } else {
        // 直式
        // eslint-disable-next-line
        if (height / width <= 16 / 9) {
            view.setDesignResolutionSize(720 * scale, 1280 * scale, ResolutionPolicy.SHOW_ALL);
        } else if (height / width <= 21 / 9) {
            view.setDesignResolutionSize(720 * scale, (height / width) * 720 * scale, ResolutionPolicy.SHOW_ALL);
        } else {
            view.setDesignResolutionSize(720 * scale, 1680 * scale, ResolutionPolicy.SHOW_ALL);
        }
    }
};

export default class OrientationManager extends EventTarget {
    public static readonly EVENT_TYPE = EventType;

    protected _allowRotation = true;

    constructor(
        public readonly supportRotation: boolean,
        protected currentOrientation: Orientation = Orientation.Landscape,
        protected readonly _adjustDesignResolution: (
            width: number,
            height: number,
            orientation: Orientation,
            scale: number,
        ) => void = defaultAdjustDesignResolution,
        protected readonly resolutionScale: number = 1,
        protected readonly overrideOrientationSettings: boolean = true,
    ) {
        super();
        this._allowRotation = supportRotation;

        if (Game.config.orientationConfig.autoDefault) {
            this.currentOrientation = window.innerWidth > window.innerHeight ? Orientation.Landscape : Orientation.Portrait;
        }

        if (overrideOrientationSettings) {
            // change screen orientation settings accordingly
            this._changeScreenOrientationSettings(supportRotation);
        }

        view.on('canvas-resize', () => {
            this.onResize();
        });
        director.on(Director.EVENT_AFTER_SCENE_LAUNCH, () => {
            this.onResize();
        });
    }

    public get current() {
        return this.currentOrientation;
    }

    protected set current(value: Orientation) {
        if (this.currentOrientation === value) {
            return;
        }
        this.currentOrientation = value;
        Game.activity.send(this.isLandscape ? ActivityEventName.RotateLandscape : ActivityEventName.RotatePortrait, {});
        this._emitOrientationChangedEvent();
    }

    public get isLandscape() {
        return this.currentOrientation === Orientation.Landscape;
    }

    public get allowRotation() {
        return this._allowRotation;
    }
    public set allowRotation(value: boolean) {
        if (!this.supportRotation && value) {
            warn('This game does not support rotation.');
            return;
        }
        if (this._allowRotation === value) {
            return;
        }
        this._allowRotation = value;
        if (value) {
            this.forcedUpdate();
        }
    }

    public forcedUpdate() {
        this.onResize();
        this._emitOrientationChangedEvent();
    }

    public setOrientationManually(specificOrientation: Orientation) {
        if (!Game.config.orientationConfig.allowManualRotation) {
            warn('Manual rotation is not allowed for this game.');
            return;
        }
        if (this.allowRotation) {
            warn('Manual rotation is not allowed while auto rotation is enabled.');
            return;
        }
        this._changeScreenOrientationSettings(false, specificOrientation);
        this.onResize(specificOrientation);
    }

    protected onResize(specificOrientation?: Orientation) {
        this.emit(OrientationManager.EVENT_TYPE.BEFORE_RESIZE);
        const size = screen.windowSize;
        const newOrientation = specificOrientation ?? this.calculateOrientation(size.width, size.height);
        this._adjustDesignResolution(size.width, size.height, newOrientation, this.resolutionScale);
        this.current = newOrientation;
        this.emit(OrientationManager.EVENT_TYPE.RESIZE);
    }

    protected calculateOrientation(width: number, height: number) {
        if (!this.allowRotation) {
            return this.current;
        }
        // rotation is not allow on PC, except ipad which is recognized as desktop device
        const isPC = !sys.isMobile && sys.os !== sys.OS.IOS;
        if (isPC && !Game.config.allowRotationOnPC) {
            return this.current;
        }
        return width > height ? Orientation.Landscape : Orientation.Portrait;
    }

    private _emitOrientationChangedEvent() {
        this.emit(OrientationManager.EVENT_TYPE.ORIENTATION_CHANGE, this.current);
        Delay.resolve(0).then(() => {
            this.emit(OrientationManager.EVENT_TYPE.ORIENTATION_CHANGE_END, this.current);
        });
    }

    private _changeScreenOrientationSettings(supportRotation: boolean, specificOrientation?: Orientation) {
        if (!supportRotation) {
            const targetOrientation = specificOrientation ?? this.currentOrientation;
            if (targetOrientation === Orientation.Landscape) {
                if (settings.querySettings('screen', 'orientation') === 'landscape') {
                    return;
                }
                view.setOrientation(macro.ORIENTATION_LANDSCAPE);
                settings.overrideSettings('screen', 'orientation', 'landscape');
            } else {
                if (settings.querySettings('screen', 'orientation') === 'portrait') {
                    return;
                }
                view.setOrientation(macro.ORIENTATION_PORTRAIT);
                settings.overrideSettings('screen', 'orientation', 'portrait');
            }
        } else {
            if (settings.querySettings('screen', 'orientation') === 'auto') {
                return;
            }
            view.setOrientation(macro.ORIENTATION_AUTO);
            settings.overrideSettings('screen', 'orientation', 'auto');
        }

        // force update once
        window.dispatchEvent(new Event('resize'));
    }
}
